import { useState } from 'react';
import { Plus, Pencil, Trash2, BedDouble, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { usePropertyEditorApi } from './api-context';
import { extractErrorMessage } from '@/lib/error';
import { showToast } from '@/lib/toast';
import type { PcUnit, PcPublishGate, UnitTypeValue } from '@/services/api/property-content.api';
import { UNIT_TYPES } from '@/services/api/property-content.api';
import { RoomFormSlideOver } from './RoomFormSlideOver';

function typeLabel(type: UnitTypeValue): string {
  return UNIT_TYPES.find((t) => t.value === type)?.label ?? type;
}

function money(value: string | null, currency: string): string {
  if (value === null) return '—';
  const n = parseFloat(value);
  if (!Number.isFinite(n)) return '—';
  return `${currency} ${n.toFixed(2)}`;
}

export function RoomsTab({
  propertyId,
  units,
  currency,
  publishGate,
}: {
  propertyId: string;
  units: PcUnit[];
  currency: string;
  publishGate: PcPublishGate;
}) {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingUnit, setEditingUnit] = useState<PcUnit | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PcUnit | null>(null);
  const { useDeleteUnit } = usePropertyEditorApi();
  const deleteMutation = useDeleteUnit(propertyId);

  function openAdd() {
    setEditingUnit(null);
    setIsFormOpen(true);
  }
  function openEdit(unit: PcUnit) {
    setEditingUnit(unit);
    setIsFormOpen(true);
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      showToast('Room removed');
      setDeleteTarget(null);
    } catch (err) {
      showToast(extractErrorMessage(err), 'error');
      setDeleteTarget(null);
    }
  }

  const priced = publishGate.pricedRoomCount;

  return (
    <div className="space-y-5">
      <div
        className={
          priced >= 1
            ? 'flex items-start gap-2 rounded-lg border border-green-200 bg-green-50 p-3'
            : 'flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3'
        }
        role="status"
      >
        {priced >= 1 ? (
          <CheckCircle2 size={18} className="text-green-600 shrink-0 mt-0.5" aria-hidden="true" />
        ) : (
          <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" aria-hidden="true" />
        )}
        <p className={priced >= 1 ? 'text-sm text-green-800' : 'text-sm text-amber-800'}>
          {priced >= 1
            ? `${priced} active priced ${priced === 1 ? 'room' : 'rooms'} — this listing meets the room requirement to go live.`
            : 'No active priced rooms yet. Add at least one active room with a base price above 0 to make the listing publishable.'}
        </p>
      </div>

      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-gray-900">Rooms &amp; rates</h2>
        <Button onClick={openAdd}>
          <Plus size={16} aria-hidden="true" />
          Add room
        </Button>
      </div>

      {units.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white">
          <EmptyState icon={BedDouble} title="No rooms yet" description="Add a room with a base price to start renting this property." actionLabel="Add room" onAction={openAdd} />
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100" aria-label="Rooms">
              <thead>
                <tr className="bg-gray-50">
                  {['Room', 'Type', 'Capacity', 'Base price', 'Weekend', 'Min stay', 'Status', ''].map((col, i) => (
                    <th key={i} scope="col" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 whitespace-nowrap">
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {units.map((u) => (
                  <tr key={u.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-semibold text-gray-900">{u.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{typeLabel(u.type)}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{u.capacity}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{money(u.basePrice, currency)}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{money(u.weekendPrice, currency)}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{u.minimumStay}</td>
                    <td className="px-4 py-3">
                      <span
                        className={
                          u.isActive
                            ? 'inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700'
                            : 'inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-500'
                        }
                      >
                        {u.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button type="button" onClick={() => openEdit(u)} className="p-2 rounded-lg text-gray-400 hover:text-brand-600 hover:bg-brand-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500" aria-label={`Edit ${u.name}`}>
                          <Pencil size={15} aria-hidden="true" />
                        </button>
                        <button type="button" onClick={() => setDeleteTarget(u)} className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500" aria-label={`Remove ${u.name}`}>
                          <Trash2 size={15} aria-hidden="true" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <RoomFormSlideOver isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} propertyId={propertyId} unit={editingUnit} />

      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Remove room?"
        message={`Remove "${deleteTarget?.name ?? ''}"? This can't be undone. Rooms with bookings can't be removed — deactivate them instead.`}
        confirmLabel="Remove room"
        isLoading={deleteMutation.isPending}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
